import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import cookieReader from 'js-cookie';

import { decodeToken, isTokenExpired } from '../auth/utils';

type FeatureFlagState = {
  defaultFeatures: Record<string, boolean>;
  regionalFeatures: Record<string, Record<string, boolean>>;
  tokenFeatures: Record<string, boolean>;
  qaFeatures: Record<string, boolean>;
  tokenFeaturesLoaded: boolean;
};

export type AppFeaturesConfig = {
  BASE?: Record<string, boolean>;
  [countryCode: string]: Record<string, boolean> | undefined;
};

const QA_FEATURE_FLAG_KEY = 'qaFeatureFLags';

const splitFeaturesConfig = (config: AppFeaturesConfig | undefined) => {
  const defaultFeatures = config?.BASE ?? {};
  const regionalFeatures = Object.entries(config ?? {}).reduce<
    Record<string, Record<string, boolean>>
  >((result, [key, value]) => {
    if (key === 'BASE' || !value) return result;

    result[key] = value;
    return result;
  }, {});

  return { defaultFeatures, regionalFeatures };
};

const getTokenFeatures = (token: string): Record<string, boolean> => {
  const decoded = decodeToken(token);

  return (decoded.features ?? []).reduce<Record<string, boolean>>(
    (result, featureName) => {
      result[featureName] = true;
      return result;
    },
    {},
  );
};

const userToken =
  cookieReader.get(import.meta.env.VITE_SECURITY_TOKEN_NAME) || '';
const isUserTokenValid = !isTokenExpired(decodeToken(userToken));

const initialState: FeatureFlagState = {
  defaultFeatures: {},
  regionalFeatures: {},
  tokenFeatures: isUserTokenValid ? getTokenFeatures(userToken) : {},
  qaFeatures: (() => {
    const raw = cookieReader.get(QA_FEATURE_FLAG_KEY) || '';

    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  })(),
  tokenFeaturesLoaded: isUserTokenValid,
};

const featureFlagsSlice = createSlice({
  name: 'featureFlags@',
  initialState,
  reducers: {
    setFeaturesConfig(state, action: PayloadAction<AppFeaturesConfig>) {
      const { defaultFeatures, regionalFeatures } = splitFeaturesConfig(
        action.payload,
      );
      state.defaultFeatures = defaultFeatures;
      state.regionalFeatures = regionalFeatures;
    },
    setTokenFeatures(state, action: PayloadAction<string>) {
      const decoded = decodeToken(action.payload);
      const isValid = !isTokenExpired(decoded);

      state.tokenFeatures = isValid ? getTokenFeatures(action.payload) : {};
      state.tokenFeaturesLoaded = isValid;
    },
    removeTokenFeatures(state) {
      state.tokenFeatures = {};
      state.tokenFeaturesLoaded = false;
    },
    toggleFeatureFlag(
      state,
      action: PayloadAction<{ featureName: string; value: boolean }>,
    ) {
      state.qaFeatures[action.payload.featureName] = action.payload.value;

      cookieReader.set(
        QA_FEATURE_FLAG_KEY,
        JSON.stringify({
          ...state.qaFeatures,
          [action.payload.featureName]: action.payload.value,
        }),
        { sameSite: 'Strict' },
      );
    },
    resetFeatureFLags(state) {
      state.qaFeatures = {};

      cookieReader.remove(QA_FEATURE_FLAG_KEY);
    },
  },
});

export default featureFlagsSlice;
